// News feeds the household can choose for the Home ticker. A fixed allow-list
// (rather than any URL) keeps the Worker from fetching arbitrary addresses.
// Each is a News screen source (shared/news.ts), fetched through the same
// pipeline and cache; the setting keeps the feed address for older copies.
// These publishers offer RSS for personal, non-commercial reading with
// attribution; re-check terms before any commercial release.

export interface NewsFeed {
  url: string;
  name: string;
  /** The News screen source with this feed. */
  source: string;
}

export const NEWS_FEEDS: NewsFeed[] = [
  { url: "https://feeds.npr.org/1001/rss.xml", name: "NPR News", source: "npr" },
  { url: "https://feeds.npr.org/1019/rss.xml", name: "NPR Technology", source: "npr-tech" },
  { url: "https://feeds.npr.org/1007/rss.xml", name: "NPR Science", source: "npr-science" },
  { url: "https://feeds.bbci.co.uk/news/world/rss.xml", name: "BBC World", source: "bbc" },
  { url: "https://feeds.bbci.co.uk/news/science_and_environment/rss.xml", name: "BBC Science", source: "bbc-science" },
  { url: "https://www.nasa.gov/news-release/feed/", name: "NASA News", source: "nasa" },
];

export function feedByUrl(url: string): NewsFeed | undefined {
  return NEWS_FEEDS.find((f) => f.url === url);
}

export function decodeEntities(s: string): string {
  return s
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, "$1")
    .replace(/<[^>]+>/g, "")
    .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
    .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
    .replace(/&quot;/g, '"')
    .replace(/&apos;|&#39;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/** Headlines from an RSS 2.0 or Atom document. */
export function parseHeadlines(xml: string, max = 8): string[] {
  const blocks = xml.match(/<(item|entry)\b[\s\S]*?<\/\1>/g) ?? [];
  return blocks
    .map((b) => /<title\b[^>]*>([\s\S]*?)<\/title>/.exec(b)?.[1] ?? "")
    .map(decodeEntities)
    .filter((t) => t.length > 8)
    .slice(0, max);
}

// "On this day" is shown to children too: skip violent or grim entries.
const GRIM = /\b(kill|killed|killing|murder|massacre|bomb|bombing|attack|shoot|shooting|shot|war|battle|invasion|invade|execut|terror|genocide|assassin|died|death|dead|crash|disaster|earthquake|hijack|riot|siege|hanged|slaughter|weapon|nuclear|explosion|sank|sinking|famine|plague|coup)\w*/i;

export function familyFriendly(text: string): boolean {
  return !GRIM.test(text);
}
